import { environment } from '@env/environment';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  inject
} from '@angular/core';

import { BaseModule } from '@gravity-angular/base';
import {
  LoadingService,
  ResponsiveService,
  SideNavModel,
  LayoutModule
} from '@gravity-angular/layout';
import { PreviousRouteService } from '@gravity-angular/utils';
import {
  AuthOptions,
  EventTypes,
  OidcSecurityService,
  PublicEventsService
} from 'angular-auth-oidc-client';
import { first, filter } from 'rxjs/operators';
import { AmplifyService } from './aws/amplify.service';
import { MatDialog } from '@angular/material/dialog';
import jwt_decode from 'jwt-decode';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { IdleLogoutDialogComponent } from './idle-logout-dialog/idle-logout-dialog.component';
import { OidcStorageService } from './auth/oidc-config/oidc-storage.service';
import { Router } from '@angular/router';
import { NotificationsService } from '@common/notifications/notifications.service';
import { XgsUmService } from './auth/xgs-service/xgs-um.service';
import {
  OidcCommonValues,
  OidcConfigIds,
  XGSRole
} from '@common/models/user-management.model';
import { UmService } from './auth/um-service/um.service';
import { lastValueFrom } from 'rxjs';
import { SharedModule } from './shared/shared.module';
import { WorkingAlertsComponent } from './working-alerts/working-alerts.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [SharedModule, BaseModule, LayoutModule, WorkingAlertsComponent],
  standalone: true
})
export class AppComponent implements OnInit {
  sideNavContent: SideNavModel;
  appName: string;
  showMainLayout: boolean;
  showSearch: boolean;
  home = { location: '/landing', text: 'Home' };
  keycloakUserData;
  userData = {
    givenName: '',
    familyName: '',
    emailAddress: '',
    id: '',
    name: '',
    roles: {},
    xgsPermissions: [],
    user_metadata: {
      locale: 'en_US'
    }
  };
  idleState = 'NOT_STARTED';
  countdown?: number = null;
  session = false;

  private readonly responsiveService = inject(ResponsiveService);
  private readonly previousRouteService = inject(PreviousRouteService);
  private readonly oidcStorageService = inject(OidcStorageService);
  private readonly eventService = inject(PublicEventsService);
  private readonly router = inject(Router);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly amplifyService = inject(AmplifyService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly loadingService = inject(LoadingService);
  private readonly xgsUmService = inject(XgsUmService);
  public dialog = inject(MatDialog);
  private readonly idle = inject(Idle);
  private readonly umService = inject(UmService);
  private readonly cd = inject(ChangeDetectorRef);

  private initializeServices(): void {
    this.previousRouteService.initialize();
    this.showMainLayout = true;
    this.showSearch = false;
    // listen for the user data changed event then update user data
    // and call the signIn function in the amplify service
    this.eventService
      .registerForEvents()
      .pipe(
        first(notification => {
          return notification.type === EventTypes.UserDataChanged;
        })
      )
      .subscribe(async value => {
        this.keycloakUserData = value?.value?.userData;
        const roles = Object.keys(this.keycloakUserData.roles);
        this.userData.givenName = this.keycloakUserData.given_name
          ? this.keycloakUserData.given_name
          : ' ';
        this.userData.familyName = this.keycloakUserData.family_name
          ? this.keycloakUserData.family_name
          : ' ';
        localStorage.setItem(
          'user',
          `${this.userData.givenName} ${this.userData.familyName}`
        );
        localStorage.setItem('email', this.keycloakUserData?.email);
        this.userData.emailAddress = this.keycloakUserData.email;
        this.userData.id = this.keycloakUserData.sub;
        this.userData.roles = this.keycloakUserData.roles;
        this.umService.setUserData(this.userData);
        this.cd.detectChanges();

        await this.xgsUmService.setXgsAuthResults();

        this.accessIperl();

        // If user has SupersetAdmin role, expose the route in side nav
        if (
          roles.some(role => {
            return role.toLowerCase().includes('superset');
          })
        ) {
          this.sideNavContent.routes.push({
            route: 'superset',
            display: 'Apache Superset',
            icon: 'all_inclusive',
            id: 'supersetDashboardLink'
          });
        }

        this.accessPipelineData();
        this.accessGSODashboard();
        this.accessManagedServicesDashboard();
        this.accessDataImport();
        this.accessMeterHistory();
        this.accessBackspool();

        // FCP Chatbot is only available in development environment
        if (!environment.production && environment.environmentName === 'dev') {
          this.accessFcpChatbot();
        }
        // SQL Generator is available in development and QA environments
        if (!environment.production) {
          this.accessSqlGenerator();
        }
        this.accessAllUsers();
        // this.addSchedulerRoute();

        const routes = new Set(this.sideNavContent.routes);
        this.sideNavContent.routes = Array.from(routes);
        this.amplifyService.signIn();
        this.cd.detectChanges();
      });

    // Listen for NewAuthenticationResult and store the new access token
    // as cookie for subdomains (i.e. superset) to use.
    this.eventService
      .registerForEvents()
      .pipe(
        filter(notification => {
          return notification.type === EventTypes.NewAuthenticationResult;
        })
      )
      .subscribe(async value => {
        await this.xgsUmService.setXgsAuthResults();

        const accessTokenObservable = this.oidcSecurityService.getAccessToken();
        accessTokenObservable.subscribe((accessToken: string) => {
          // eslint-disable-next-line dot-notation
          const expirationDate = new Date(
            jwt_decode(accessToken)['exp'] * 1000
          );
        });
      });

    this.idle.setIdle(1680); // time in seconds (13 mins) before user is notified of idle state
    this.idle.setTimeout(120); // time in seconds (2 mins) folowing notification before user is logged off
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // provide sources that will "interrupt" aka provide events indicating the user is active

    // When user is idle dialog will open to warn user
    this.idle.onIdleStart.subscribe(() => {
      this.idleState = 'IDLE';
      const dialogRef = this.dialog.open(IdleLogoutDialogComponent, {
        width: '550px'
      });
      dialogRef.afterClosed();
    });

    // Subscribes to detect when user is no longer idle
    this.idle.onIdleEnd.subscribe(() => {
      this.idleState = 'NOT_IDLE';
      this.countdown = null;
      this.cd.detectChanges();
    });

    // Subscribes to detect when a users session is timing out
    this.idle.onTimeout.subscribe(() => {
      this.idleState = 'TIMED_OUT';
      this.logout();
    });

    this.notificationsService.getLoadingState().subscribe(value => {
      this.loadingService.showLoading(value);
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.responsiveService.checkWidth();
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilitychange() {
    if (document.hidden && localStorage.getItem('state') === 'invalid') {
      this.oidcStorageService.clear();
    }
  }

  ngOnInit(): void {
    this.initializeServices();
    this.reset();
    this.appName = environment.appName;

    this.home = {
      location: '/',
      text: 'Home'
    };

    this.sideNavContent = {
      mode: 'side',
      opened: false,
      routes: [
        {
          route: 'user/user-detail',
          display: 'User Profile',
          icon: 'account_box',
          id: 'userDetailLink'
        }
      ]
    };

    this.userData.givenName = ' ';
    this.userData.familyName = ' ';

    window.onstorage = () => {
      if (
        localStorage.getItem('state') === 'invalid' ||
        localStorage.getItem('state') === 'validating'
      ) {
        setTimeout(() => {
          sessionStorage.clear();
          this.router.navigate(['/login']);
        }, 4000);
      }
    };

    setTimeout(() => {
      this.session = true;
    }, 123000);
  }

  reset() {
    this.idle.watch();
    this.idleState = 'NOT_IDLE';
    this.countdown = null;
  }

  toggleSideNav(opened?: boolean): void {
    this.sideNavContent.opened =
      opened === undefined ? !this.sideNavContent.opened : opened;
  }

  /**
   * User is logged out and redirect url updated based on token lifecycle
   */
  async logout(): Promise<void> {
    sessionStorage.removeItem('route');
    localStorage.removeItem(OidcCommonValues.TAB_DETECTION);
    await this.amplifyService.awsSignOut();

    const customParams: AuthOptions = {
      /**
       * customParams handles event of missing id_token_hint during logout
       * @param url URL to be handled
       */
      urlHandler: url => {
        const newUrl = url.replace('id_token_hint=undefined', '');
        window.location.href = newUrl;
        localStorage.removeItem(OidcConfigIds.XGS);
      }
    };
    await lastValueFrom(
      this.oidcSecurityService.logoff(OidcConfigIds.XGS, customParams)
    );
  }

  getUserData(): object {
    return this.userData;
  }

  accessAllUsers(): void {
    const accessUsersRoute = {
      route: 'user/user-list',
      display: 'Users List',
      icon: 'supervisor_account',
      id: 'userListLink'
    };
    const roleListAccess = {
      route: 'role/role-list',
      display: 'Roles List',
      icon: 'verified_user',
      id: 'roleListLink'
    };
    for (const role of Object.keys(this.userData.roles)) {
      if (role === 'DataLakeANDDataScienceTeam') {
        this.sideNavContent.routes.push(accessUsersRoute);
      }
      if (role === 'DataLakeANDDataScienceTeam') {
        this.sideNavContent.routes.push(roleListAccess);
      }
    }
  }

  accessIperl(): void {
    const Iperl = {
      route: 'iperl/iperloverview',
      display: 'IPerl EMEA Dashboard',
      icon: 'dashboard',
      id: 'iperldashboardLink'
    };
    for (const role of Object.keys(this.userData.roles)) {
      if (role === 'OXI:APPLICATIONADMIN' || role === 'ipearlEMEA') {
        this.sideNavContent.routes.push(Iperl);
      }
    }
  }

  accessPipelineData(): void {
    const pipelineData = {
      route: 'pipeline-data-table',
      display: 'Pipeline Data Table',
      icon: 'dataset',
      id: 'pipeline-data-table'
    };
    for (const role of Object.keys(this.userData.roles)) {
      if (role === 'OXI:APPLICATIONADMIN' || role === 'PipelineDataTable') {
        this.sideNavContent.routes.push(pipelineData);
      }
    }
  }

  accessGSODashboard(): void {
    const gsoDatawarehouse = {
      route: 'gsodatawarehouse',
      display: 'GSO Data Warehouse',
      icon: 'storage',
      id: 'gsodatawarehouse'
    };
    for (const role of Object.keys(this.userData.roles)) {
      if (
        role === 'OXI:APPLICATIONADMIN' ||
        role === 'DataLakeANDDataScienceTeam' ||
        role === 'SupersetGSODataWarehouse'
      ) {
        this.sideNavContent.routes.push(gsoDatawarehouse);
      }
    }
  }

  accessManagedServicesDashboard(): void {
    const managedServices = {
      route: 'managedservices',
      display: 'Managed Services',
      icon: 'query_stats',
      id: 'managedservices'
    };
    for (const role of Object.keys(this.userData.roles)) {
      if (
        role === 'OXI:APPLICATIONADMIN' ||
        role === 'DataLakeANDDataScienceTeam' ||
        role === 'SupersetGSOAMISaaS'
      ) {
        this.sideNavContent.routes.push(managedServices);
      }
    }
  }

  accessDataImport(): void {
    const dataImport = {
      route: 'data-import',
      display: 'Data Import',
      icon: 'cloud_upload',
      id: 'dataimport'
    };
    for (const role of Object.keys(this.userData.roles)) {
      if (
        role === 'OXI:APPLICATIONADMIN' ||
        role === 'DataLakeANDDataScienceTeam' ||
        role === 'SupersetGSODataWarehouse'
      ) {
        this.sideNavContent.routes.push(dataImport);
      }
    }
  }

  accessMeterHistory(): void {
    const meterHistory = {
      route: 'meter-history',
      display: 'Meter History',
      icon: 'history',
      id: 'meterHistoryLink'
    };
    for (const role of Object.keys(this.userData.roles)) {
      if (
        role === 'OXI:APPLICATIONADMIN' ||
        role === 'DataLakeANDDataScienceTeam'
      ) {
        this.sideNavContent.routes.push(meterHistory);
      }
    }
  }

  accessBackspool(): void {
    const backspool = {
      route: 'backspools',
      display: 'Backspools',
      icon: 'history',
      id: 'backspoolLink'
    };
    for (const role of Object.keys(this.userData.roles)) {
      if (
        role === 'OXI:APPLICATIONADMIN' ||
        role === 'DataLakeANDDataScienceTeam'
      ) {
        this.sideNavContent.routes.push(backspool);
      }
    }
  }

  async addSchedulerRoute(): Promise<void> {
    const schedulerRoute = {
      route: 'scheduler',
      display: 'Scheduler',
      icon: 'schedule',
      id: 'schedulerLink'
    };

    const roles = await this.getXgsRoles(this.userData.id);
    const hasAccess = roles?.some(role => {
      return (
        role.name === 'OXI:APPLICATIONADMIN' ||
        role.permissions.some(permission => {
          return permission.includes('scheduler');
        })
      );
    });
    if (!hasAccess) {
      return;
    }

    if (
      !this.sideNavContent.routes.some(route => {
        return route.route === 'scheduler';
      })
    ) {
      this.sideNavContent.routes.push(schedulerRoute);
    }
  }

  async getXgsRoles(userId): Promise<XGSRole[]> {
    return new Promise((resolve, reject) => {
      this.xgsUmService.getUserRoles(userId).subscribe(
        roles => {
          resolve(roles);
        },
        () => {
          reject([]);
        }
      );
    });
  }

  /**
   * Provides access to FCP Chatbot feature
   * Only available in development environment
   */
  accessFcpChatbot(): void {
    const fcpChatbot = {
      route: 'sensus-genai-assistant',
      display: 'Sensus Engineer GenAI Assistant',
      icon: 'chat',
      id: 'fcpChatbotLink'
    };

    // Restrict access to specific roles
    for (const role of Object.keys(this.userData.roles)) {
      if (
        role === 'OXI:APPLICATIONADMIN' ||
        role === 'DataLakeANDDataScienceTeam' ||
        role === 'sensusGenAIAssistant'
      ) {
        this.sideNavContent.routes.push(fcpChatbot);
      }
    }
  }

  /**
   * Provides access to SQL Generator feature
   * Available in development and QA environments
   */
  accessSqlGenerator(): void {
    const sqlGenerator = {
      route: 'sql-generator',
      display: 'SQL Generator',
      icon: 'code',
      id: 'sqlGeneratorLink'
    };

    // Grant access to users with specific roles
    for (const role of Object.keys(this.userData.roles)) {
      if (
        role === 'OXI:APPLICATIONADMIN' ||
        role === 'DataLakeANDDataScienceTeam' ||
        role === 'sqlGenerator'
      ) {
        this.sideNavContent.routes.push(sqlGenerator);
      }
    }
  }
}
